import { useCallback, useContext, useEffect, useState } from 'react';
import {
  Button,
  Divider,
  Form,
  InputNumber,
  Layout,
  Modal,
  Select,
  Space,
  Table,
  message,
} from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import { API } from '../../../api';
import { ParcelStatusContext } from '../../../context/ParcelStatusProvider';
import CustomerSelect from '../../../lib/CustomerSelect';

const DELIVERY_TYPES = [
  { value: 'PICKUP', label: 'Салбараас авах' },
  { value: 'HOMEDELIVERY', label: 'Гараас гарт' },
];

export const DeliveryTime = () => {
  const { statusList } = useContext(ParcelStatusContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [customers, setCustomers] = useState<any[]>([]);
  const [areas, setAreas] = useState<any[]>([]);
  const [form] = Form.useForm();

  useEffect(() => {
    // loadCustomers();
    loadAreas();
  }, []);

  useEffect(() => {
    fetchData(location.search);
  }, [location.search]);

  const fetchData = useCallback(async (search: string) => {
    try {
      setLoading(true);
      const response = await API.get({ apiVersion: 'api' })(`/settings/time-period/list${search}`);
      setData(response.result?.data ?? []);
    } catch {
    } finally {
      setLoading(false);
    }
  }, []);

  const changeSearch = useCallback(
    (val: any) => {
      const res = { ...queryString.parse(location.search), ...val };
      navigate(`?${queryString.stringify(res)}`);
    },
    [navigate, location.search]
  );

  const loadCustomers = async () => {
    try {
      const response = await API.get({ apiVersion: 'api' })(`/customer/list`);
      setCustomers(response.result?.data ?? response.result ?? []);
    } catch {}
  };

  const loadAreas = async () => {
    try {
      const response = await API.get({ apiVersion: 'api' })(`/settings/branch_area/list`);
      setAreas(response.result?.data ?? []);
    } catch {}
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      await API.post({ apiVersion: 'api' })(`/settings/time-period/create`, values);
      message.success('Амжилттай хадгаллаа');
      setOpen(false);
      form.resetFields();
      fetchData(location.search);
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error('Хадгалахад алдаа гарлаа');
    } finally {
      setSaving(false);
    }
  };

  const params = queryString.parse(location.search);
  const flatAreas = areas?.flatMap((a: any) => a.areas ?? []);

  const columns = [
    {
      title: 'Төлөв',
      dataIndex: ['parcelStatus', 'name'],
      key: 'parcelStatus',
    },
    {
      title: 'Хүргэлтийн төрөл',
      dataIndex: 'deliveryType',
      key: 'deliveryType',
      render: (val: any) => val?.text,
    },
    {
      title: 'Харилцагч',
      dataIndex: ['customer', 'name'],
      key: 'customer',
      render: (val: string) => val ?? 'Үндсэн төрөл',
    },
    {
      title: 'Хүргэлтийн бүс',
      dataIndex: ['area', 'name'],
      key: 'area',
      render: (val: string) => val ?? '-',
    },
    {
      title: 'Цаг',
      dataIndex: 'timePeriod',
      key: 'timePeriod',
    },
  ];

  return (
    <Layout.Content style={{ margin: '24px 16px 0' }}>
      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
        <h3 style={{ margin: 0 }}>Хүргэх цагийн хязгаар</h3>
        <Button type="primary" onClick={() => setOpen(true)}>
          Нэмэх
        </Button>
      </Space>
      <Divider />
      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          placeholder="Хүргэлтийн төрөл"
          allowClear
          style={{ width: 180 }}
          options={DELIVERY_TYPES}
          value={(params.deliveryType as string) || undefined}
          onChange={(val) => changeSearch({ deliveryType: val ?? undefined })}
        />
        <Select
          placeholder="Хүргэлтийн бүс"
          allowClear
          style={{ width: 200 }}
          options={flatAreas.map((a: any) => ({ value: a.no, label: a.name }))}
          value={params.areaNo ? Number(params.areaNo) : undefined}
          onChange={(val) => changeSearch({ areaNo: val ?? undefined })}
        />
      </Space>
      <Table loading={loading} dataSource={data} columns={columns} rowKey="no" />

      <Modal
        title="Цагийн хязгаар нэмэх"
        open={open}
        onCancel={() => {
          setOpen(false);
          form.resetFields();
        }}
        onOk={handleSubmit}
        confirmLoading={saving}
        okText="Хадгалах"
        cancelText="Болих"
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Төлөв"
            name="parcelStatus"
            rules={[{ required: true, message: 'Төлөв сонгоно уу' }]}
          >
            <Select
              placeholder="Төлөв сонгох"
              options={statusList.map((s: any) => ({ value: s.cd, label: s.name }))}
            />
          </Form.Item>
          <Form.Item
            label="Хүргэлтийн төрөл"
            name="deliveryType"
            rules={[{ required: true, message: 'Хүргэлтийн төрөл сонгоно уу' }]}
          >
            <Select placeholder="Хүргэлтийн төрөл сонгох" options={DELIVERY_TYPES} />
          </Form.Item>
          <Form.Item label="Харилцагч" name="customerNo">
            <CustomerSelect showSearch allowClear />
          </Form.Item>
          <Form.Item label="Хүргэлтийн бүс" name="courierAreaNo">
            <Select
              placeholder="Бүс сонгох"
              allowClear
              options={flatAreas.map((a: any) => ({ value: a.no, label: a.name }))}
            />
          </Form.Item>
          <Form.Item
            label="Цаг"
            name="timePeriod"
            rules={[{ required: true, message: 'Цаг оруулна уу' }]}
          >
            <InputNumber style={{ width: '100%' }} min={0} placeholder="Цаг" />
          </Form.Item>
        </Form>
      </Modal>
    </Layout.Content>
  );
};
